import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Copy, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ESTATUS_POSICION,
  ETIQUETA_ESTATUS_POSICION,
  formatearFecha,
  type EstatusPosicion,
} from "@/lib/catalogos";
import {
  cambiarEstatusPosicion,
  duplicarPosicion,
  listarPosicionesAdmin,
} from "@/lib/posiciones.functions";

export const Route = createFileRoute("/_authenticated/admin/posiciones/")({
  component: PosicionesAdmin,
});

function PosicionesAdmin() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "posiciones"],
    queryFn: () => listarPosicionesAdmin(),
  });

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ["admin"] });
    queryClient.invalidateQueries({ queryKey: ["posiciones"] });
  };

  const cambiar = useMutation({
    mutationFn: (variables: { id: string; estatus: EstatusPosicion }) =>
      cambiarEstatusPosicion({ data: variables }),
    onSuccess: () => {
      invalidar();
      toast.success("Estatus actualizado.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const duplicar = useMutation({
    mutationFn: (id: string) => duplicarPosicion({ data: { id } }),
    onSuccess: () => {
      invalidar();
      toast.success("Posición duplicada como borrador.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <Loader2 className="size-6 animate-spin text-primary" />;
  if (error) return <p className="text-sm text-destructive">{(error as Error).message}</p>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl">Posiciones</h1>
          <p className="text-sm text-muted-foreground">
            Crea, publica y da seguimiento a las posiciones de la convocatoria.
          </p>
        </div>
        <Button asChild>
          <Link to="/admin/posiciones/$id" params={{ id: "nueva" }}>
            Nueva posición
          </Link>
        </Button>
      </div>

      {!data?.length ? (
        <p className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
          Todavía no has creado posiciones.
        </p>
      ) : (
        <ul className="space-y-3">
          {data.map((posicion) => (
            <li
              key={posicion.id}
              className="flex flex-col gap-4 rounded-2xl border border-border/70 bg-card p-5 shadow-sm md:flex-row md:items-center"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="font-display text-lg">{posicion.nombre}</h2>
                  <Badge variant="secondary">{posicion.area}</Badge>
                  <Badge variant="outline">{posicion.turno}</Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {posicion.vacantes} vacante(s) · {posicion.total_postulaciones} postulación(es) ·
                  Fecha límite: {formatearFecha(posicion.fecha_limite)}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Select
                  value={posicion.estatus}
                  onValueChange={(valor) =>
                    cambiar.mutate({ id: posicion.id, estatus: valor as EstatusPosicion })
                  }
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ESTATUS_POSICION.map((estatus) => (
                      <SelectItem key={estatus} value={estatus}>
                        {ETIQUETA_ESTATUS_POSICION[estatus]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button asChild size="sm" variant="outline">
                  <Link to="/admin/posiciones/$id" params={{ id: posicion.id }}>
                    <Pencil className="mr-1 size-4" /> Editar
                  </Link>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => duplicar.mutate(posicion.id)}
                  disabled={duplicar.isPending}
                >
                  <Copy className="mr-1 size-4" /> Duplicar
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
